/**
 * Goals-area breadcrumb origins. Built on the generic `from`-origin helpers in
 * `breadcrumbOrigin.ts`; this module only owns the goals-specific matcher table.
 *
 * The goal detail page (`/goal/:id` and `/company/goal/:id`) roots its breadcrumb on
 * the list the user came from, carried in the `from` query param. When there is no
 * recognized origin, the page falls back to an owner-aware root.
 */
import { getFromLabel, resolveOrigin } from "./breadcrumbOrigin";

export { appendCurrentLocationAsFrom } from "./breadcrumbOrigin";

type GoalsOriginMatcher = {
	match: string;
	label: string;
};

// Order matters: more specific paths first.
const ORIGIN_MATCHERS: GoalsOriginMatcher[] = [
	{ match: "/goals/teamGoals", label: "My Team's Goals" },
	{ match: "/goals/mygoals", label: "My Goals" },
	{ match: "/company/goals/list", label: "Company Goals" },
	{ match: "/company/goals/dashboard", label: "Company Goals Overview" },
	{ match: "/company/goals/alignment", label: "Company Goals Alignment" },
];

export type GoalsOrigin = {
	/** Sanitized internal `from` path (empty string when absent or invalid). */
	from: string;
	/** Label for the originating page, or null when there is no recognized origin. */
	label: string | null;
};

export const parseGoalsOrigin = (search: string): GoalsOrigin => {
	const { from, matcher } = resolveOrigin(search, ORIGIN_MATCHERS);
	if (!from) return { from: "", label: null };
	// An explicit label (the review/meeting the goal was opened from) wins over the list matchers.
	const explicitLabel = getFromLabel(search);
	if (explicitLabel) return { from, label: explicitLabel };
	return { from, label: matcher?.label ?? null };
};
